import { memo, useState } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { usePartnershipData } from './usePartnershipData.jsx';

export const ContributingCentersChart = memo(function ContributingCentersChart() {
  const { centerMapByFunding, localFilteredResults } = usePartnershipData();
  const yAxisWidth = useResponsiveYAxisWidth(140);
  const [tip, setTip] = useState('');
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });

  if (!centerMapByFunding || centerMapByFunding.size === 0) return (
    <ChartWrapper title="Centers">
      <NoChartData message="No center data available" detail="This chart shows CGIAR centers contributing to results, split by funding type. None of the results in the current selection have center data." />
    </ChartWrapper>
  );

  const chartData = [...centerMapByFunding.values()]
    .sort((a, b) => b.resultCount - a.resultCount)
    .slice(0, 16)
    .map(c => ({
      name: c.code.length > 20 ? c.code.substring(0, 18) + '...' : c.code,
      fullName: c.fullName || c.code,
      w1w2: c.w1w2Count,
      w3: c.w3Count,
    }));

  const renderStackTotal = (props) => {
    const { x, y, width, height } = props;
    const entry = chartData[props.index];
    if (!entry) return null;
    const total = (entry.w1w2 || 0) + (entry.w3 || 0);
    return (
      <text x={x + width + 4} y={y + height / 2} {...LABEL_STYLE} dominantBaseline="middle">
        {total}
      </text>
    );
  };

  return (
    <ChartWrapper title="Centers" info="CGIAR centers contributing to this program's results, ranked by contribution count and split by funding type." chartData={chartData} sourceResults={localFilteredResults} exportFilename="centers">
      <ResponsiveContainer width="100%" height={Math.max(300, chartData.length * 30)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 15, right: 30 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
          <YAxis type="category" dataKey="name" width={yAxisWidth}
            tick={({ x, y, payload }) => {
              const entry = chartData.find(d => d.name === payload.value);
              return (
                <g transform={`translate(${x},${y})`}
                  style={{ cursor: 'default' }}
                  onMouseEnter={(e) => { setTip(entry?.fullName || payload.value); setTipPos({ x: e.clientX, y: e.clientY }); }}
                  onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTip('')}
                >
                  <text x={-4} y={0} dy={4} textAnchor="end" fontSize={yAxisWidth < 140 ? 9 : 11} fill="#666">{payload.value}</text>
                </g>
              );
            }}
          />
          <Tooltip content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const d = payload[0].payload;
            const total = (d.w1w2 || 0) + (d.w3 || 0);
            return (
              <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.6 }}>
                <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{d.fullName}</div>
                {d.w1w2 > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#006837', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#4b5563' }}>W1/W2 Pooled: <strong>{d.w1w2.toLocaleString()}</strong></span>
                  </div>
                )}
                {d.w3 > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#F37021', display: 'inline-block', flexShrink: 0 }} />
                    <span style={{ color: '#4b5563' }}>W3/Bilateral: <strong>{d.w3.toLocaleString()}</strong></span>
                  </div>
                )}
                <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #e5e7eb', color: '#6b7280', fontSize: 12 }}>Total: {total.toLocaleString()}</div>
              </div>
            );
          }} />
          <Legend
            formatter={(value) => value === 'w1w2' ? 'W1/W2 Pooled' : 'W3/Bilateral'}
            wrapperStyle={{ fontSize: 12 }}
          />
          <Bar dataKey="w1w2" stackId="a" fill="#006837" />
          <Bar dataKey="w3" stackId="a" fill="#F37021" radius={[0, 4, 4, 0]}>
            <LabelList content={renderStackTotal} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {tip && (
        <div className="fixed z-50 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none"
             style={{ left: tipPos.x + 12, top: tipPos.y - 28 }}>
          {tip}
        </div>
      )}
    </ChartWrapper>
  );
});
